import { observer } from 'mobx-react';
import React, { ReactNode } from 'react';
import { ResultAwareTask } from '~common/helper/mobx-tasks';
import { DataError } from '~common/components/core/data-error.component';
import { DataLoading } from '~web/view/components/core/data-loading.component';

type ResolvedProps<T> = (result: T) => ReactNode;
type ErrorProps = (error: Error) => ReactNode | string | void;
type LoadingProps = () => ReactNode | string | void;
type InitialProps = () => ReactNode;

interface Props<T> {
  task: ResultAwareTask<T>;
  children?: ResolvedProps<T>;
  renderError?: ErrorProps;
  renderLoading?: LoadingProps;
  renderInitial?: InitialProps;
}

/**
 * This component receives a `Task` and renders the children when resolved.
 * Or, you can also use an object as a child to manually manage the three states.
 *
 * Note: This component doesn't handle executing the task for now.
 */

export const TaskObserver: <T>(props: Props<T>) => React.ReactElement<Props<T>> = observer(
  ({ task, children, renderError, renderLoading, renderInitial }) => {
    const renderPending = (): ReactNode | string | void => {
      if (renderLoading && typeof renderLoading === 'function') {
        return renderLoading();
      }

      if (renderLoading && typeof renderLoading === 'string') {
        return <DataLoading>{renderLoading}</DataLoading>;
      }

      return <DataLoading />;
    };

    const renderRejected = (error?: Error): ReactNode | string | void => {
      if (renderError && typeof renderError === 'function') {
        return renderError(error);
      }

      if (renderError && typeof renderError === 'string') {
        return <DataError error={renderError} />;
      }

      return <DataError error={error} />;
    };

    const renderResolved = (result: any): ReactNode => {
      return children(result);
    };

    const renderInitializing = (): ReactNode => {
      if (renderInitial) {
        return renderInitial();
      }

      return null;
    };

    return (
      <>
        {task.pending && !task.result && renderPending()}
        {task.rejected && renderRejected(task.error)}
        {task.result && renderResolved(task.result)}
        {!task.rejected && !task.pending && !task.result && renderInitializing()}
      </>
    );
  },
);



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/web/src/js/view/components/core/task-observer.component.tsx